<template>
	<div v-if="cache && Object.keys(cache).length">
		<div :class="{ 'p4 bg5 r2 bc7 b1': isBoxed }">
			<!-- Render fields -->
			<div v-for="(field, i) in computedSchema" :key="i" :class="`mb${gap}`">
				<form-field
					:name="field.entity + field.model + i"
					:componentName="field.component"
					:label="field.label"
					:text="field.entity"
					v-bind="field.props"
					v-model="form[field.model]"
					:size="size"
					@blur="updateField(field)"
					:caseId="$route.params.id"
					:proposalId="$store.state.caseManagement.proposalId"
					:pricing="cache && cache.totalPrice ? cache.totalPrice : {}"
					:hasPremiumActions="true"
					:hasDiscountActions="true"
					:hasDiscountLogic="false"
					:hasLoadingActions="false"
					@premium:updated="fetchProposal"
					@premium:added="fetchProposal"
					@discount:updated="fetchProposal"
					@discount:added="fetchProposal"
					@discount:deleted="fetchProposal"
					@loadings:updated="fetchProposal"
					@loadings:added="fetchProposal"
					@loadings:deleted="fetchProposal"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import FormField from "./FormField/FormField.vue"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import { computeSchema } from "../utils/computeSchema.js"

export default {
	components: { FormField },

	props: {
		/**
		 * Schema for the form
		 */
		schema: {
			type: Array,
			required: false,
			default: () => []
		},

		/**
		 * Size of the form inputs
		 */
		size: {
			validator(val) {
				return ["xsmall", "small", "medium", "large"].includes(val)
			},
			default: "medium"
		},

		/**
		 * Vertical gap between fields
		 * It translates into mb{gap} class
		 */
		gap: {
			type: String,
			default: "4"
		},

		/**
		 * If this is true, entity will have a class that creates a visual box around the
		 * whole entity form
		 */
		isBoxed: {
			type: Boolean,
			default: true
		}
	},

	data() {
		return {
			cache: {},
			form: {}
		}
	},

	computed: {
		/**
		 * Schema with proper component names, added model
		 */
		computedSchema() {
			return computeSchema(this.schema)
		},

		proposalId_() {
			return this.$store.state.caseManagement.proposalId
		}
	},

	methods: {
		async fetchProposal() {
			const query = gql`
				query proposal($where: caseWhere) {
					cases(where: $where) {
						list {
							proposals {
								id
								status
								basket {
									id
								}
								totalPrice {
									amount
									currencyCode
									formattedPrice
									originalPrice
									formattedOriginalPrice
									appliedTaxes {
										code
										name
										ratio
										flat
										formattedFlat
										order
										formattedOriginalPrice
										originalPrice
									}
									appliedDiscounts {
										id
										code
										ratio
										flat
										formattedFlat
										order
										formattedOriginalPrice
										originalPrice
									}
									loadings {
										id
										codeName {
											code
											name
										}
										ratio
										flat
									}
								}
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: { where: { id: this.$store.state.caseManagement.caseId } }
			})

			// Populate cache
			this.cache = {
				...(res?.data?.cases?.list?.[0]?.proposals?.find(
					proposal => proposal.id === this.proposalId_
				) || {})
			}

			// Populate form
			// TODO: this should be refactored to take into account facts etc.
			// For now we are just populating root fields
			this.form = { ...this.cache }
		},

		async updateField(field) {
			// Facts
			// if (field?.bindTo?.includes("proposal.facts")) return await this.upsertFactHandler(field)

			// Root fields
			await this.proposalHandler(field)
		},

		async proposalHandler(field) {
			const query = gql`
				mutation updateProposal(
					$caseId: String!
					$proposalId: String!
					$input: updateProposalInput!
				) {
					updateProposal(
						caseId: $caseId
						proposalId: $proposalId
						input: $input
					) {
						errors
						status
					}
				}
			`
			// Check if data actually changed compared to cache
			if (this.form[field.model] === this?.cache?.[field.model]) return

			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					proposalId: this.$store.state.caseManagement.proposalId,
					input: {
						[field.model]: this.form[field.model]
							? this.form[field.model]
							: null
					}
				}
			})

			if (res?.data?.updateProposal?.status !== "success") {
				this.showError(field.label)
				return Promise.resolve()
			}

			// Update cache
			this.$set(this.cache, field.model, this.form[field.model])
			return Promise.resolve()
		},

		showError(fieldName) {
			// Helper function to add error message
			// TODO: localize
			this.$store.dispatch("showError", [
				`Field ${fieldName} wasn't saved, please update it again`
			])
		}
	},

	watch: {
		proposalId_: {
			handler() {
				this.fetchProposal()
			},
			immediate: false
		}
	},

	mounted() {
		this.fetchProposal()
	}
}
</script>
